/**
 * MKS-136 — the ticket's billing period on the task detail ("must be delivered
 * for payment 2").
 *
 * Sits next to the Milestone field and is deliberately NOT the same thing: the
 * milestone is the sprint the ticket is in, this is the payment it has to be
 * delivered for, and a ticket commonly has both. That is the whole reason
 * billing periods are a separate concept rather than a kind of milestone.
 *
 * Who may change it is decided by the schedule itself (`canEdit` from
 * `GET /projects/:id/billing-periods` — admin, finance or the project's PM), so
 * this component asks the same source the schedule editor does rather than
 * guessing from a board permission. Everybody else sees the value read-only.
 *
 * With no schedule, the whole field renders nothing.
 */
import { useState } from 'react';
import { useBillingSchedule, useSetTaskBillingPeriod } from '../../hooks/useBillingPeriods';
import { formatCurrency, formatDisplayDate } from '../../utils/format';

interface BillingPeriodFieldProps {
  taskId: number;
  projectId: number;
  currentBillingPeriodId: number | null | undefined;
  /** False for an external viewer or while the detail is in edit mode. */
  canEdit: boolean;
  onError?: (message: string) => void;
}

export function BillingPeriodField({
  taskId,
  projectId,
  currentBillingPeriodId,
  canEdit,
  onError,
}: BillingPeriodFieldProps) {
  const { data, error } = useBillingSchedule(projectId);
  const setMutation = useSetTaskBillingPeriod();
  const [pending, setPending] = useState(false);

  const schedule = data?.data;
  // No schedule, or the project simply has no periods: say nothing rather
  // than showing an empty picker.
  if (error || !schedule || schedule.periods.length === 0) return null;

  const current = schedule.periods.find((p) => p.id === currentBillingPeriodId) ?? null;
  // A cancelled period is not offered, but IS still shown when the ticket
  // already points at one — hiding it would silently misreport the ticket.
  const selectable = schedule.periods.filter(
    (p) => p.status !== 'cancelled' || p.id === currentBillingPeriodId,
  );
  const editable = canEdit && schedule.canEdit;

  const handleChange = async (value: string) => {
    const billingPeriodId = value === '' ? null : Number(value);
    setPending(true);
    try {
      await setMutation.mutateAsync({ taskId, billingPeriodId });
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="pt-2 border-t border-ms-border-subtle">
      <label
        htmlFor={`task-billing-period-${taskId}`}
        className="block text-xs font-medium text-ms-text-secondary mb-1"
      >
        Billing period
      </label>
      {editable ? (
        <select
          id={`task-billing-period-${taskId}`}
          value={currentBillingPeriodId ? String(currentBillingPeriodId) : ''}
          disabled={pending}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">—</option>
          {selectable.map((period) => (
            <option key={period.id} value={String(period.id)}>
              {period.name}
              {period.plannedAmount !== null
                ? ` · ${formatCurrency(period.plannedAmount, false, period.currency ?? 'CZK')}`
                : ''}
            </option>
          ))}
        </select>
      ) : (
        <div className="text-sm text-ms-text" data-testid="task-billing-period-value">
          {current ? (
            <>
              {current.name}
              <span className="ml-1 text-xs text-ms-text-secondary">
                (due {formatDisplayDate(current.billedTo)})
              </span>
            </>
          ) : (
            <span className="text-ms-text-secondary">—</span>
          )}
        </div>
      )}
    </div>
  );
}
